/**
 * SignatureBehaviorMap — 6 iconic, recognizable Buddy behaviors.
 * Each is a "trademark emote" with distinct motion identity, timing,
 * anticipation beats, and clear emotional purpose.
 *
 * Priority (high → low):
 *   reliefExhale > feeFeverTwitch > minerPrayer > queueStare > laserFlex > crabDeadpan
 */

import { ShortTermState } from '@/lib/types/mood';
import { LongTermState } from '@/lib/types/mood';
import { EyeShape, MouthShape } from '@/config/buddyExpressions';

export type SignatureBehavior =
  | 'queueStare'
  | 'minerPrayer'
  | 'reliefExhale'
  | 'crabDeadpan'
  | 'laserFlex'
  | 'feeFeverTwitch';

// ── Animation phases: anticipation → action → follow-through ──

export interface SignaturePhase {
  body: { y?: number[]; x?: number[]; scaleX?: number[]; scaleY?: number[]; rotate?: number[] };
  duration: number;
  ease: string | number[];
}

export interface SignatureBehaviorConfig {
  id: SignatureBehavior;
  label: string;
  meaning: string;
  priority: number; // higher = more important, overrides lower
  cooldownMs: number; // minimum time before this specific behavior can replay
  // Multi-phase animation: anticipation → action → settle
  phases: SignaturePhase[];
  totalDuration: number;
  // Face overrides
  eyeOverride?: 'halfClosed' | 'bright' | 'wide' | 'lookUp' | 'worried' | 'deadpan' | 'star';
  mouthOverride?: 'flat' | 'worried' | 'relieved' | 'tense' | 'grin' | 'deadpanLine' | 'smallSmile' | 'smug';
  // Pupil control
  pupilOverride?: { x: number; y: number };
  pupilDrift?: { xRange: number; yRange: number; speed: number };
  // Blink pattern during behavior
  blinkPattern?: 'hold' | 'slow' | 'double' | 'long';
  // Aura modifiers
  auraMod?: { brightnessMultiplier: number; tensionMultiplier: number };
  // Body shape override during behavior
  bodyShapeOverride?: { scaleX?: number; scaleY?: number; borderRadius?: string };
}

// ═════════════════════════════════════════════════════
// THE 6 SIGNATURE BEHAVIORS
// ═════════════════════════════════════════════════════

export const SIGNATURE_BEHAVIORS: Record<SignatureBehavior, SignatureBehaviorConfig> = {

  // ── 1. QUEUE STARE ──
  // Buddy notices the queue building. Turns attention sideways.
  // Focused stillness with a slight tilt. Character acting: concentration.
  queueStare: {
    id: 'queueStare',
    label: 'Queue Stare',
    meaning: 'Watching the queue build',
    priority: 30,
    cooldownMs: 35000,  // was 25s — less frequent
    phases: [
      // Anticipation: slight pause, body tenses
      { body: { scaleY: [1, 1.01], scaleX: [1, 0.99] }, duration: 400, ease: 'easeOut' },
      // Action: lean + tilt toward queue
      { body: { x: [0, 3.5], rotate: [0, 3.5], scaleY: [1.01, 1.01] }, duration: 600, ease: [0.25, 0.1, 0.25, 1] },
      // Hold: focused stillness — this IS the signature
      { body: { x: [3.5, 3.5, 3], rotate: [3.5, 3.5, 3] }, duration: 2200, ease: 'linear' },
      // Return
      { body: { x: [3, 0], rotate: [3, 0], scaleY: [1.01, 1], scaleX: [0.99, 1] }, duration: 800, ease: [0.45, 0.05, 0.55, 0.95] },
    ],
    totalDuration: 4000,
    eyeOverride: 'bright',
    mouthOverride: 'flat',
    pupilOverride: { x: 2.5, y: 0.3 },
    blinkPattern: 'hold', // no blinking during focus
    auraMod: { brightnessMultiplier: 0.8, tensionMultiplier: 1.3 },
    bodyShapeOverride: { scaleX: 0.97, scaleY: 1.02 },
  },

  // ── 2. MINER PRAYER ──
  // Long block wait. Buddy looks upward with hope/tension.
  // The stillness and upward gaze is what makes it recognizable.
  minerPrayer: {
    id: 'minerPrayer',
    label: 'Miner Prayer',
    meaning: 'Waiting for a block',
    priority: 40,
    cooldownMs: 40000,  // was 30s
    phases: [
      // Anticipation: settle down, gather weight
      { body: { y: [0, 2], scaleY: [1, 0.98], scaleX: [1, 1.02] }, duration: 500, ease: 'easeOut' },
      // Action: slow rise, looking up
      { body: { y: [2, -5], scaleY: [0.98, 1.03], scaleX: [1.02, 0.97] }, duration: 800, ease: [0.25, 0.1, 0.25, 1] },
      // Hold: suspended pause — the "prayer"
      { body: { y: [-5, -4.5, -5, -4.5], scaleY: [1.03, 1.02, 1.03, 1.02] }, duration: 2500, ease: 'easeInOut' },
      // Settle back
      { body: { y: [-4.5, 0], scaleY: [1.02, 1], scaleX: [0.97, 1] }, duration: 700, ease: [0.45, 0.05, 0.55, 0.95] },
    ],
    totalDuration: 4500,
    eyeOverride: 'lookUp',
    mouthOverride: 'worried',
    pupilOverride: { x: 0, y: -2 },
    blinkPattern: 'slow', // slow, tense blinks
    auraMod: { brightnessMultiplier: 0.65, tensionMultiplier: 1.6 },
    bodyShapeOverride: { scaleX: 0.96, scaleY: 1.04 },
  },

  // ── 3. RELIEF EXHALE ──
  // Pressure just released. Block found during congestion, or mempool clearing.
  // The exhale movement (compress → release) is the key signature.
  reliefExhale: {
    id: 'reliefExhale',
    label: 'Relief Exhale',
    meaning: 'Pressure just eased',
    priority: 60, // highest — relief is dramatic
    cooldownMs: 25000,  // was 20s
    phases: [
      // Anticipation: brief hold/tense
      { body: { scaleY: [1, 0.94], scaleX: [1, 1.06], y: [0, 3] }, duration: 400, ease: 'easeOut' },
      // Action: exhale upward, body stretches
      { body: { scaleY: [0.94, 1.06], scaleX: [1.06, 0.95], y: [3, -7] }, duration: 600, ease: [0.34, 1.56, 0.64, 1] },
      // Follow-through: gentle settle with overshoot
      { body: { scaleY: [1.06, 0.99, 1.01, 1], scaleX: [0.95, 1.02, 0.99, 1], y: [-7, 1, -2, 0] }, duration: 1500, ease: [0.45, 0.05, 0.55, 0.95] },
    ],
    totalDuration: 2500,
    eyeOverride: 'halfClosed', // eyes close during exhale
    mouthOverride: 'relieved',
    blinkPattern: 'long', // long eyes-closed moment
    auraMod: { brightnessMultiplier: 1.4, tensionMultiplier: 0.2 },
    bodyShapeOverride: { scaleX: 1.03, scaleY: 0.97, borderRadius: '50% 50% 54% 46% / 48% 48% 52% 52%' },
  },

  // ── 4. FEE FEVER TWITCH ──
  // Sudden fee spike. Sharp, involuntary reaction.
  // Speed and nervousness define this behavior.
  feeFeverTwitch: {
    id: 'feeFeverTwitch',
    label: 'Fee Fever Twitch',
    meaning: 'Fee pressure just increased',
    priority: 50,
    cooldownMs: 20000,  // was 12s — don't spam twitches
    phases: [
      // No anticipation — it's sudden
      // Action: sharp twitch
      { body: { x: [0, -3, 3.5, -2, 1.5, 0], scaleX: [1, 1.04, 0.97, 1.02, 0.99, 1], scaleY: [1, 0.96, 1.03, 0.98, 1.01, 1] }, duration: 500, ease: [0.34, 1.56, 0.64, 1] },
      // Follow-through: residual tension
      { body: { x: [0, -0.5, 0.5, 0], scaleY: [1, 0.99, 1.005, 1] }, duration: 700, ease: 'easeOut' },
    ],
    totalDuration: 1200,
    eyeOverride: 'wide',
    mouthOverride: 'tense',
    blinkPattern: 'double', // rapid double blink
    auraMod: { brightnessMultiplier: 1.15, tensionMultiplier: 1.7 },
    bodyShapeOverride: { scaleX: 1.03, scaleY: 0.96 },
  },

  // ── 5. LASER FLEX ──
  // Market feels strong. Confident, lifted posture.
  // Power and composure define this — not bouncing, just STANDING TALL.
  laserFlex: {
    id: 'laserFlex',
    label: 'Laser Flex',
    meaning: 'Market feels strong',
    priority: 25,
    cooldownMs: 40000,  // was 30s
    phases: [
      // Anticipation: slight crouch
      { body: { y: [0, 2], scaleY: [1, 0.97], scaleX: [1, 1.02] }, duration: 300, ease: 'easeOut' },
      // Action: confident lift
      { body: { y: [2, -6], scaleY: [0.97, 1.05], scaleX: [1.02, 0.96] }, duration: 500, ease: [0.34, 1.56, 0.64, 1] },
      // Hold: power pose — tall and still
      { body: { y: [-6, -5.5, -6], scaleY: [1.05, 1.04, 1.05] }, duration: 1500, ease: 'easeInOut' },
      // Settle
      { body: { y: [-6, -2, 0], scaleY: [1.05, 1.01, 1], scaleX: [0.96, 0.99, 1] }, duration: 700, ease: [0.45, 0.05, 0.55, 0.95] },
    ],
    totalDuration: 3000,
    eyeOverride: 'star',
    mouthOverride: 'grin',
    blinkPattern: 'hold',
    auraMod: { brightnessMultiplier: 1.5, tensionMultiplier: 0.15 },
    bodyShapeOverride: { scaleX: 0.96, scaleY: 1.05 },
  },

  // ── 6. CRAB DEADPAN ──
  // Nothing is happening in price. The humor IS the stillness.
  // Intentionally minimal — the joke is how little Buddy does.
  crabDeadpan: {
    id: 'crabDeadpan',
    label: 'Crab Deadpan',
    meaning: 'Still here.',
    priority: 10, // lowest — ambient behavior
    cooldownMs: 55000,  // was 45s — crab is ambient, very rare
    phases: [
      // Slow, heavy settle — like sighing into nothing
      { body: { y: [0, 1.5], scaleY: [1, 0.98], scaleX: [1, 1.01] }, duration: 1200, ease: [0.45, 0.05, 0.55, 0.95] },
      // The stare — almost nothing happens. That's the point.
      { body: { x: [0, 0.3, -0.3, 0], y: [1.5, 1.5] }, duration: 3000, ease: 'linear' },
      // Barely return
      { body: { y: [1.5, 0.5, 0], scaleY: [0.98, 0.99, 1], scaleX: [1.01, 1, 1] }, duration: 1000, ease: 'easeInOut' },
    ],
    totalDuration: 5200,
    eyeOverride: 'deadpan',
    mouthOverride: 'smug',
    blinkPattern: 'long', // one slow heavy blink
    auraMod: { brightnessMultiplier: 0.5, tensionMultiplier: 0 },
    bodyShapeOverride: { scaleX: 1.03, scaleY: 0.97 },
  },
};

// ═════════════════════════════════════════════════════
// PRIORITY QUEUE & COOLDOWN ORCHESTRATOR
// ═════════════════════════════════════════════════════

export class SignatureOrchestrator {
  private lastPlayedAt: Partial<Record<SignatureBehavior, number>> = {};
  private globalCooldownUntil = 0;
  private currentBehavior: SignatureBehavior | null = null;
  private currentExpiresAt = 0;

  /** Global cooldown after any signature plays (prevents spam).
   *  Increased from 8s to 12s for more breathing room. */
  private static GLOBAL_COOLDOWN_MS = 12000;

  /** Check if a specific behavior is off cooldown */
  private isOffCooldown(behavior: SignatureBehavior, now: number): boolean {
    const config = SIGNATURE_BEHAVIORS[behavior];
    const lastPlayed = this.lastPlayedAt[behavior] || 0;
    return now - lastPlayed >= config.cooldownMs;
  }

  /** Can any signature play right now? */
  canPlay(now: number): boolean {
    return now >= this.globalCooldownUntil && now >= this.currentExpiresAt;
  }

  /** Get current active behavior (if still playing) */
  getActive(now: number): SignatureBehavior | null {
    if (this.currentBehavior && now < this.currentExpiresAt) {
      return this.currentBehavior;
    }
    if (this.currentBehavior && now >= this.currentExpiresAt) {
      this.currentBehavior = null;
    }
    return null;
  }

  /** Try to play a behavior. Returns true if accepted. */
  play(behavior: SignatureBehavior, now: number): boolean {
    if (!this.canPlay(now)) {
      // Allow override if new behavior has higher priority than current
      if (this.currentBehavior) {
        const currentPriority = SIGNATURE_BEHAVIORS[this.currentBehavior].priority;
        const newPriority = SIGNATURE_BEHAVIORS[behavior].priority;
        if (newPriority <= currentPriority) return false;
      } else {
        return false;
      }
    }

    if (!this.isOffCooldown(behavior, now)) return false;

    const config = SIGNATURE_BEHAVIORS[behavior];
    this.currentBehavior = behavior;
    this.currentExpiresAt = now + config.totalDuration;
    this.lastPlayedAt[behavior] = now;
    this.globalCooldownUntil = now + config.totalDuration + SignatureOrchestrator.GLOBAL_COOLDOWN_MS;

    return true;
  }

  /** Force-end current behavior (e.g., when a transient event fires) */
  interrupt(): void {
    this.currentBehavior = null;
    this.currentExpiresAt = 0;
  }
}

// ═════════════════════════════════════════════════════
// TRIGGER EVALUATION (with orchestrator)
// ═════════════════════════════════════════════════════

export function evaluateSignatureBehavior(
  shortTerm: ShortTermState,
  longTerm: LongTermState,
  timeSinceLastBlockSec: number,
  congestionDelta: number,
  orchestrator: SignatureOrchestrator,
): SignatureBehavior | null {
  const now = Date.now();

  // If something is still playing, return it
  const active = orchestrator.getActive(now);
  if (active) return active;

  // Collect candidates with their behaviors
  const candidates: SignatureBehavior[] = [];

  // Relief Exhale: easing after congestion (priority 60)
  if (shortTerm.networkState === 'easing' && shortTerm.buddyMood === 'relieved') {
    candidates.push('reliefExhale');
  }

  // Fee Fever Twitch: congested + stressed (priority 50)
  if (shortTerm.networkState === 'congested' && shortTerm.buddyMood === 'stressed') {
    candidates.push('feeFeverTwitch');
  }

  // Miner Prayer: long wait (priority 40)
  if (timeSinceLastBlockSec > 900 && shortTerm.blockState === 'delayed') {
    candidates.push('minerPrayer');
  }

  // Queue Stare: building mempool (priority 30)
  if (shortTerm.networkState === 'building' && congestionDelta > 2) {
    candidates.push('queueStare');
  }

  // Laser Flex: bullish (priority 25)
  if (longTerm.marketMood === 'SUSTAINED_BULLISH' && shortTerm.priceState === 'pumping') {
    candidates.push('laserFlex');
  }

  // Crab Deadpan: flat market (priority 10)
  if (longTerm.marketMood === 'SUSTAINED_CRAB' && shortTerm.priceState === 'flat') {
    candidates.push('crabDeadpan');
  }

  // Sort by priority (highest first) and try to play
  candidates.sort((a, b) => SIGNATURE_BEHAVIORS[b].priority - SIGNATURE_BEHAVIORS[a].priority);

  for (const candidate of candidates) {
    if (orchestrator.play(candidate, now)) {
      return candidate;
    }
  }

  return null;
}
